/**
 * Institution Service
 *
 * API client for institution and rating data from BigQuery.
 * All responses are typed for TypeScript safety.
 */

import { get, post } from './api';
import type {
  RatedInstitutionsResponse,
  RatedInstitutionsWithRatingsResponse,
  LatestRatingResponse,
  RatingHistoryResponse,
  IndicatorScoresResponse,
  CacheStatsResponse,
  CacheClearResponse,
  RatedInstitution,
  RatedInstitutionWithRating,
  InstitutionRating,
  RatingHistoryEntry,
  IndicatorScore,
} from '../types/institution';

// ============================================================================
// INSTITUTION ENDPOINTS
// ============================================================================

/**
 * Get all rated institutions with metadata (name, location, segment)
 * Cached for 24 hours on the backend.
 */
export async function getRatedInstitutions(): Promise<RatedInstitution[]> {
  const response = await get<RatedInstitutionsResponse>('/api/institutions/rated');
  return response.institutions;
}

/**
 * Get all rated institutions with their latest ratings
 * Optimized for map/list views. Cached for 24 hours on the backend.
 * @param periods - Optional array of periods in YYYY-MM format for historical data
 */
export async function getRatedInstitutionsWithRatings(
  periods?: string[]
): Promise<RatedInstitutionWithRating[]> {
  let url = '/api/institutions/rated-with-ratings';
  if (periods && periods.length > 0) {
    url += `?periods=${periods.join(',')}`;
  }
  const response = await get<RatedInstitutionsWithRatingsResponse>(url);
  return response.institutions;
}

// ============================================================================
// RATING ENDPOINTS
// ============================================================================

/**
 * Get the latest rating for a specific institution
 * Returns null if no rating exists.
 */
export async function getLatestRating(institutionId: string): Promise<InstitutionRating | null> {
  const response = await get<LatestRatingResponse>(
    `/api/institutions/${encodeURIComponent(institutionId)}/ratings/latest`
  );
  return response.rating;
}

/**
 * Get rating history for a specific institution
 * Returns all historical ratings sorted by period descending.
 */
export async function getRatingHistory(institutionId: string): Promise<RatingHistoryEntry[]> {
  const response = await get<RatingHistoryResponse>(
    `/api/institutions/${encodeURIComponent(institutionId)}/ratings`
  );
  return response.ratings;
}

// ============================================================================
// INDICATOR ENDPOINTS
// ============================================================================

/**
 * Get indicator scores for a specific institution and period
 * @param institutionId - Institution ID (e.g., "COOP_0190155722001")
 * @param period - Period in YYYY-MM format (e.g., "2025-05")
 */
export async function getIndicatorScores(
  institutionId: string,
  period: string
): Promise<IndicatorScore[]> {
  const response = await get<IndicatorScoresResponse>(
    `/api/institutions/${encodeURIComponent(institutionId)}/indicators?period=${encodeURIComponent(period)}`
  );
  return response.indicators;
}

// ============================================================================
// ADMIN ENDPOINTS
// ============================================================================

/**
 * Get cache statistics (for monitoring)
 */
export async function getCacheStats(): Promise<CacheStatsResponse['stats']> {
  const response = await get<CacheStatsResponse>('/api/institutions/cache/stats');
  return response.stats;
}

/**
 * Clear all institution/rating caches (platform admin only)
 * Should be called after uploading new data to BigQuery.
 */
export async function clearCache(): Promise<CacheClearResponse['cleared']> {
  const response = await post<CacheClearResponse>('/api/institutions/cache/clear');
  return response.cleared;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Find an institution by ID from the rated list
 */
export async function findInstitutionById(
  institutionId: string
): Promise<RatedInstitutionWithRating | null> {
  const institutions = await getRatedInstitutionsWithRatings();
  return institutions.find((i) => i.institution_id === institutionId) || null;
}

/**
 * Get institutions filtered by segment
 */
export async function getInstitutionsBySegment(
  segmentId: string
): Promise<RatedInstitutionWithRating[]> {
  const institutions = await getRatedInstitutionsWithRatings();
  return institutions.filter((i) => i.segment_id === segmentId);
}

/**
 * Get institutions filtered by province
 */
export async function getInstitutionsByProvince(
  province: string
): Promise<RatedInstitutionWithRating[]> {
  const institutions = await getRatedInstitutionsWithRatings();
  return institutions.filter((i) => i.province === province);
}

/**
 * Get available provinces from rated institutions
 */
export async function getProvinces(): Promise<string[]> {
  const institutions = await getRatedInstitutions();
  const provinces = new Set(institutions.map((i) => i.province));
  return Array.from(provinces).sort();
}

/**
 * Get available segments from rated institutions
 */
export async function getSegments(): Promise<Array<{ id: string; name: string }>> {
  const institutions = await getRatedInstitutions();
  const segmentMap = new Map<string, string>();
  for (const inst of institutions) {
    if (!segmentMap.has(inst.segment_id)) {
      segmentMap.set(inst.segment_id, inst.segment_name);
    }
  }
  return Array.from(segmentMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default {
  // Core endpoints
  getRatedInstitutions,
  getRatedInstitutionsWithRatings,
  getLatestRating,
  getRatingHistory,
  getIndicatorScores,
  // Admin endpoints
  getCacheStats,
  clearCache,
  // Utility functions
  findInstitutionById,
  getInstitutionsBySegment,
  getInstitutionsByProvince,
  getProvinces,
  getSegments,
};
